import * as XLSX from "xlsx";
import { uniqueID } from "./common";
import { getTreeArray } from "../actions/report";

export const findNode = (node, nodeId, callback) => {
  if (node.id === nodeId) {
    callback(node);
    return;
  }
  if (node.children) node.children.forEach((element) => findNode(element, nodeId, callback));
};

// Get Node Path — searches across all home (root) nodes
export const getNodePath = (treeData = [], nodeId, getPathString = false) => {
  const roots = Array.isArray(treeData) ? treeData : [treeData];

  // Find a node by id across every root
  const findInRoots = (id) => {
    let found;
    roots.forEach((root) => root && findNode(root, id, (node) => (found = node)));
    return found;
  };

  let item = findInRoots(nodeId);

  let path = [];
  while (item && item.parentId !== null) {
    path.unshift({ id: item.id, name: item.name });
    item = findInRoots(item.parentId);
  }
  item && path.unshift({ id: item.id, name: item.name });

  return getPathString ? path.map((node) => node.name).join(" > ") : path;
};

// Tree Animation
export const treeMotion = {
  motionAppear: false,
  motionName: "node-motion",
  onEnterActive: (node) => ({ height: node.scrollHeight }),
  onLeaveStart: (node) => ({ height: node.offsetHeight }),
};

// Default Tree
export const getDefaultTree = () => {
  return { treeData: [{ id: uniqueID(), name: "Home", isLeaf: true, parentId: null, children: [] }] };
};

// filter right answers becoz user may have update the tree and current node may not be the leaf node.
export const filterAnswers = (treeData = [], answers) => {
  const roots = Array.isArray(treeData) ? treeData : [treeData];
  answers = answers.filter((nodeId) => {
    let item;
    roots.forEach((root) => root && findNode(root, nodeId, (node) => (item = node)));
    if (item && item.children && item.children.length === 0) return nodeId;
    return "";
  });
  return answers;
};

// Convert JSON to TreeData
export const jsonToTreeData = (jsonArray) => {
  const data = {};
  jsonArray.forEach((item) => {
    let node = { id: uniqueID(), name: "", isLeaf: true, parentId: null, children: [] };
    item.forEach((childItem, index) => {
      childItem = childItem.toString().trim();
      if (childItem) {
        let indexData = data[index] || [];
        node.name = childItem;

        // Get Parent [Last index]
        let parentData = data[index - 1];
        if (parentData) {
          let parent = parentData[parentData.length - 1];
          node.parentId = parent.id;
          parent.isLeaf = false;
          parent.children.push(node);
        }
        indexData.push(node);

        data[index] = indexData;
      }
    });
  });

  return data[0];
};

// Export the tree
export const exportTree = (name = "", treeData = []) => {
  return new Promise((resolve, reject) => {
    try {
      const wb = XLSX.utils.book_new();
      const treeInfo = getTreeArray(treeData);

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(treeInfo), "Tree Details");
      XLSX.writeFile(wb, name + ".xlsx");

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Filters a react-d3-tree dataset by removing nodes where visited === false.
 * @param {Object} node - A single tree node.
 * @returns {Object|null} - Filtered node or null if excluded.
 */
export const filterTreeByVisitedNode = (node, isRoot = false) => {
  // Hard exit if node is not visited → node is removed.
  // Root (home) nodes are always kept — they are the entry points and with
  // multiple home nodes only the traversed one would otherwise be "visited".
  if (!isRoot && !node.visited) return null;

  // If node has children, process recursively (children are never roots)
  if (node.children && node.children.length > 0) {
    const filteredChildren = node.children
      .map((child) => filterTreeByVisitedNode(child)) // process each child
      .filter(Boolean); // remove nulls

    return { ...node, children: filteredChildren };
  }

  // Leaf node (or root) → keep it
  return node;
};

// Collect ids of expandable nodes (those that have children) — used by Expand All.
// When `rootsOnly` is true, only the home/root node ids that have children are returned.
export function getAllNodeIds(treeData = [], rootsOnly = false) {
  const ids = [];
  const walk = (nodes) => {
    nodes.forEach((node) => {
      if (node?.id && node?.children?.length) {
        ids.push(node.id);
        if (!rootsOnly) walk(node.children);
      }
    });
  };
  walk(treeData);
  return ids;
}

// Collect ids of every node with an empty/whitespace name (recursively)
export function getEmptyNodeIds(treeData = []) {
  const ids = [];
  const walk = (nodes) => {
    nodes.forEach((node) => {
      if (!node?.name || !node.name.trim()) ids.push(node?.id);
      if (node?.children?.length) walk(node.children);
    });
  };
  walk(treeData);
  return ids;
}

// Function to find the item position in an array
export function loopData(data, id, callback) {
  return data.forEach((item, index, arr) => {
    if (item.id === id) {
      callback(item, index, arr);
      return;
    }

    if (item.children) loopData(item.children, id, callback);
  });
}

// Every node of the tree, depth first
export const flattenTree = (treeData = []) => treeData.flatMap((node) => [node, ...flattenTree(node.children)]);

// Tree data shaped for antd <Tree>; getProps adds per-node overrides (title, className, ...)
export const toAntdTreeNodes = (treeData = [], getProps = () => ({})) =>
  treeData.map((node) => ({
    ...node,
    key: node.id,
    title: node.name,
    isLeaf: !node.children?.length,
    children: toAntdTreeNodes(node.children, getProps),
    ...getProps(node),
  }));
